/**
 * In-memory last-good copy of the employee's My leave data, so switching
 * tabs back to My leave shows the figures immediately (the screen unmounts
 * on every tab switch — App.tsx swaps ESS screens by route) while
 * `useMyLeave` re-fetches in the background: stale-while-revalidate.
 *
 * ── Whose data this is ─────────────────────────────────────────────────────
 * Leave balances and request history are personal data, and a phone can be
 * shared. So:
 *   - the cache lives in module memory ONLY — never `localStorage` or any
 *     other storage that outlives the page (docs/security-rules.md);
 *   - it belongs to exactly one user: every read and write names the
 *     signed-in user id, a read for anyone else returns nothing, and a write
 *     for a different user wipes the previous owner's entries first;
 *   - `null` user id (no signed-in user known) never reads or writes;
 *   - `AuthProvider` claims it for whoever signs in, clears it when nobody
 *     is signed in, and sign-out clears it outright via `performSignOut`
 *     (mirrors `device_notifications.ts`);
 *   - `useMyLeave` forgets it when a load says the session can no longer
 *     read the data (UNAUTHORIZED / FORBIDDEN / step-up).
 */
import type {
  MyLeaveBalance,
  MyLeaveRequest,
  MyLeaveType,
} from '../screens/my_leave/my_leave_utils.js';

export interface MyLeaveCachedLink {
  entity_id: string;
  entity_name: string;
}

export interface MyLeaveCachedEntityData {
  balances: MyLeaveBalance[];
  leave_types: MyLeaveType[];
  requests: MyLeaveRequest[];
}

interface CacheState {
  owner_user_id: string;
  links: MyLeaveCachedLink[] | undefined;
  selected_entity_id: string | null;
  by_entity: Map<string, MyLeaveCachedEntityData>;
}

let cache: CacheState | null = null;

/** The cache for `user_id`, (re)created empty if someone else owns it. */
function owned_by(user_id: string): CacheState {
  if (cache?.owner_user_id !== user_id) {
    cache = {
      owner_user_id: user_id,
      links: undefined,
      selected_entity_id: null,
      by_entity: new Map(),
    };
  }
  return cache;
}

export interface MyLeaveCacheSnapshot {
  links: MyLeaveCachedLink[] | undefined;
  selected_entity_id: string | null;
}

/** The cached employer links + selected employer for `user_id`, if any. */
export function read_my_leave_links(
  user_id: string | null,
): MyLeaveCacheSnapshot | null {
  if (user_id === null || cache?.owner_user_id !== user_id) return null;
  return { links: cache.links, selected_entity_id: cache.selected_entity_id };
}

/** The cached balances/types/requests for one employer of `user_id`. */
export function read_my_leave_entity_data(
  user_id: string | null,
  entity_id: string,
): MyLeaveCachedEntityData | null {
  if (user_id === null || cache?.owner_user_id !== user_id) return null;
  return cache.by_entity.get(entity_id) ?? null;
}

export function write_my_leave_links(
  user_id: string | null,
  links: MyLeaveCachedLink[],
  selected_entity_id: string | null,
): void {
  if (user_id === null) return;
  const state = owned_by(user_id);
  state.links = links;
  state.selected_entity_id = selected_entity_id;
  // Drop figures for employers the user is no longer linked to.
  const linked = new Set(links.map((l) => l.entity_id));
  for (const entity_id of [...state.by_entity.keys()]) {
    if (!linked.has(entity_id)) state.by_entity.delete(entity_id);
  }
}

export function write_my_leave_entity_data(
  user_id: string | null,
  entity_id: string,
  data: MyLeaveCachedEntityData,
): void {
  if (user_id === null) return;
  owned_by(user_id).by_entity.set(entity_id, data);
}

/**
 * Bind the cache to `user_id`. A different user than the current owner
 * wipes it, so nobody signing in on a shared phone sees the previous
 * user's leave.
 */
export function claim_my_leave_cache(user_id: string): void {
  owned_by(user_id);
}

/** Sign-out: drop everything. */
export function clear_my_leave_cache(): void {
  cache = null;
}

/**
 * Drop `user_id`'s copy: their session can no longer read it (expired,
 * access withdrawn). Leaves another owner's cache alone; `null` is a no-op.
 */
export function forget_my_leave_cache(user_id: string | null): void {
  if (user_id !== null && cache?.owner_user_id === user_id) cache = null;
}
